import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SearchInput } from '@/components/ui/SearchInput';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Page } from '@/design-system-v2/page';
import { cn } from '@/lib/utils';

import { PersonRow } from './components/person-row';
import { StatusRow } from './components/status-row';
import { ConnectionRequests, RowConnectionButton } from './connections';
import { TrustedCell, TrustedHead, useCreatePersonMutation } from './edits';
import { filterPeople, withoutRequestOnly } from './filter-people';
import { PeopleGroups } from './groups';
import { usePeopleWikiQuery } from './people-wiki.query';
import {
  WIKI_PROVIDER_LABELS,
  type WikiPerson,
  type WikiProvider,
} from './people-wiki.types';

const COLUMN_COUNT = 6;

const sourceNames = (people: WikiPerson[]): string[] => {
  const providers = new Set(
    people.flatMap((person) =>
      person.accounts
        .filter(({ source }) => source === 'sync')
        .map(({ provider }) => provider)
    )
  );
  return Object.entries(WIKI_PROVIDER_LABELS)
    .filter(([provider]) => providers.has(provider as WikiProvider))
    .map(([, label]) => label);
};

const SOURCE_LIST_FORMAT = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction',
});

const HEAD_CLASS_NAME =
  'h-10 bg-surface px-4 text-xs font-normal text-foreground-subtle';

export const PeopleWiki = observer(() => {
  const { data: allPeople = [], isLoading, isError } = usePeopleWikiQuery();
  const [query, setQuery] = useState('');
  const { mutate: createPerson, isPending: isCreating } =
    useCreatePersonMutation();
  const people = withoutRequestOnly(allPeople);
  const filteredPeople = filterPeople(people, query);

  const sources = sourceNames(people);
  const peopleCount = `${people.length} ${people.length === 1 ? 'person' : 'people'}`;
  let subtitle: string | null = null;
  if (!isLoading && !isError && people.length > 0) {
    subtitle =
      sources.length === 0
        ? peopleCount
        : `${peopleCount} from ${SOURCE_LIST_FORMAT.format(sources)}`;
  }

  let body;
  if (isLoading) {
    body = <StatusRow message="Loading people…" columnCount={COLUMN_COUNT} />;
  } else if (isError) {
    body = (
      <StatusRow message="Could not load people" columnCount={COLUMN_COUNT} />
    );
  } else if (people.length === 0) {
    body = <StatusRow message="No people yet" columnCount={COLUMN_COUNT} />;
  } else if (filteredPeople.length === 0) {
    body = (
      <StatusRow
        message="No people match your search"
        columnCount={COLUMN_COUNT}
      />
    );
  } else {
    body = filteredPeople.map((person) => (
      <PersonRow
        key={person.id}
        person={person}
        connectionCell={<RowConnectionButton person={person} />}
        trustedCell={<TrustedCell person={person} />}
      />
    ));
  }

  return (
    <YStack className="h-full overflow-hidden">
      <YStack
        className={cn(
          'min-h-0 flex-1 pb-8',
          cn(Page.column({ width: 'full' }), 'gap-4 pt-1')
        )}
      >
        <ConnectionRequests />
        <PeopleGroups />
        <YStack className="gap-2">
          <XStack className="items-center gap-2">
            {subtitle !== null && (
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search people..."
                aria-label="Search people"
                className="w-72 flex-none"
              />
            )}
            <Button
              variant="secondary"
              size="sm"
              isLoading={isCreating}
              onClick={() => createPerson()}
            >
              <Icons.plus className="size-3.5" />
              New person
            </Button>
          </XStack>
          {subtitle !== null && (
            <XStack className="items-center text-xs text-foreground-subtle">
              {subtitle}
            </XStack>
          )}
        </YStack>
        <YStack className="min-h-0 overflow-hidden rounded-xl border border-border">
          <Table
            wrapperClassName="min-h-0"
            className="min-w-[60rem] table-fixed"
          >
            <colgroup>
              <col className="w-[24%]" />
              <col className="w-[14%]" />
              <col className="w-[16%]" />
              <col className="w-[24%]" />
              <col className="w-44" />
              <col className="w-28" />
            </colgroup>
            <TableHeader className="sticky top-0 z-10 border-b border-border bg-surface">
              <TableRow className="border-none">
                <TableHead className={cn(HEAD_CLASS_NAME, 'pl-5')}>
                  Person
                </TableHead>
                <TableHead className={HEAD_CLASS_NAME}>Also known as</TableHead>
                <TableHead className={HEAD_CLASS_NAME}>Other emails</TableHead>
                <TableHead className={HEAD_CLASS_NAME}>Accounts</TableHead>
                <TableHead className={HEAD_CLASS_NAME}>Connection</TableHead>
                <TableHead className={cn(HEAD_CLASS_NAME, 'pr-5')}>
                  <TrustedHead />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{body}</TableBody>
          </Table>
        </YStack>
      </YStack>
    </YStack>
  );
});
